import React, { useEffect, useState } from 'react';
import { uploads as uploadsApi, projects as projectsApi } from '@/utils/api';
import { FolderOpen, Search, Trash2, FileText, Video, Image as ImageIcon, Download, X, UploadCloud } from 'lucide-react';
import { toast } from 'sonner';
import AnnotatedVideoPlayer from '@/components/AnnotatedVideoPlayer';

const KINDS = [
  { k: '', label: 'All files' },
  { k: 'image', label: 'Images' },
  { k: 'video', label: 'Videos' },
  { k: 'document', label: 'Documents' },
];

function kindOf(ct) {
  if (!ct) return 'other';
  if (ct.startsWith('image/')) return 'image';
  if (ct.startsWith('video/')) return 'video';
  if (/pdf|msword|officedocument|text\/|sheet|presentation/.test(ct)) return 'document';
  return 'other';
}

function FileIcon({ ct, size = 18 }) {
  const k = kindOf(ct);
  const color = k === 'video' ? '#5B2D8E' : k === 'image' ? '#3C8A5A' : k === 'document' ? '#006080' : '#5C6B72';
  const Icon = k === 'video' ? Video : k === 'image' ? ImageIcon : FileText;
  return <Icon size={size} style={{ color }} />;
}

export default function Documents() {
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [kind, setKind] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);

  const reload = () => {
    setLoading(true);
    uploadsApi.list({
      kind: kind || undefined,
      project_id: projectFilter || undefined,
      q: q || undefined,
      limit: 200,
    }).then(r => setItems(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [kind, projectFilter, q]);
  useEffect(() => { projectsApi.list().then(r => setProjects(r.data)).catch(() => {}); }, []);

  const del = async (id) => {
    if (!window.confirm('Delete this file permanently? This also removes it from Cloudflare R2.')) return;
    try { await uploadsApi.delete(id); toast.success('Deleted'); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="p-6 animate-fade-in" data-testid="documents-page">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-btn flex items-center justify-center" style={{ background: '#006080' + '20' }}>
          <FolderOpen size={18} style={{ color: '#006080' }} />
        </div>
        <div>
          <h1 className="text-xl font-black" style={{ color: 'var(--ink)' }}>Documents Library</h1>
          <p className="text-sm" style={{ color: 'var(--muted)' }}>All uploads across projects — docs, images, video demos</p>
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted)' }} />
          <input className="input-agency pl-8 text-sm" placeholder="Search by file name..." value={q} onChange={e => setQ(e.target.value)} data-testid="docs-search" />
        </div>
        <select className="input-agency text-sm w-40" value={kind} onChange={e => setKind(e.target.value)} data-testid="docs-kind">
          {KINDS.map(k => <option key={k.k} value={k.k}>{k.label}</option>)}
        </select>
        <select className="input-agency text-sm w-48" value={projectFilter} onChange={e => setProjectFilter(e.target.value)} data-testid="docs-project">
          <option value="">All projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map(i => <div key={i} className="h-40 rounded-card animate-pulse" style={{ background: 'var(--border)' }} />)}
        </div>
      ) : items.length === 0 ? (
        <div className="card-base p-10 text-center">
          <UploadCloud size={32} style={{ color: 'var(--border)', margin: '0 auto 10px' }} />
          <p className="text-sm font-medium" style={{ color: 'var(--ink)' }}>No files yet</p>
          <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>
            Files uploaded via wizards, documents tab, or project vault will show up here once R2 is enabled.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="docs-grid">
          {items.map(f => {
            const k = kindOf(f.content_type);
            const proj = projects.find(p => p.id === f.project_id);
            return (
              <div key={f.id} className="card-interactive p-0 overflow-hidden" onClick={() => setPreview(f)} data-testid={`doc-${f.id}`}>
                <div className="h-28 flex items-center justify-center overflow-hidden relative" style={{ background: 'var(--bg)' }}>
                  {k === 'image' ? (
                    <img src={f.public_url} alt={f.original_name} className="w-full h-full object-cover" />
                  ) : k === 'video' ? (
                    f.thumbnail_url ? (
                      <>
                        <img src={f.thumbnail_url} alt={f.original_name} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.25)' }}>
                          <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: 'rgba(91,45,142,0.9)' }}>
                            <Video size={14} style={{ color: 'white' }} />
                          </div>
                        </div>
                        {f.annotations?.length > 0 && (
                          <span className="absolute top-1 right-1 text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: '#5B2D8E', color: 'white' }}>
                            {f.annotations.length} note{f.annotations.length !== 1 ? 's' : ''}
                          </span>
                        )}
                      </>
                    ) : (
                      <div className="relative w-full h-full">
                        <video src={f.public_url} className="w-full h-full object-cover" muted preload="metadata" />
                        <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(91,45,142,0.15)' }}>
                          <Video size={28} style={{ color: '#5B2D8E' }} />
                        </div>
                      </div>
                    )
                  ) : (
                    <FileIcon ct={f.content_type} size={32} />
                  )}
                </div>
                <div className="p-3">
                  <p className="text-xs font-semibold truncate" style={{ color: 'var(--ink)' }}>{f.original_name}</p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[10px]" style={{ color: 'var(--muted)' }}>{proj?.name || (f.tags?.includes('wizard') ? 'Wizard upload' : '—')}</span>
                    <span className="text-[10px] font-mono" style={{ color: 'var(--muted)' }}>{(f.size / 1024).toFixed(0)}kb</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }} onClick={() => setPreview(null)} data-testid="doc-preview">
          <div className="max-w-4xl w-full rounded-card overflow-hidden flex flex-col" style={{ background: 'var(--surface)', maxHeight: '90vh' }} onClick={e => e.stopPropagation()}>
            <div className="p-4 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border)' }}>
              <div className="flex items-center gap-2 min-w-0">
                <FileIcon ct={preview.content_type} />
                <p className="text-sm font-bold truncate" style={{ color: 'var(--ink)' }}>{preview.original_name}</p>
              </div>
              <div className="flex items-center gap-2">
                <a href={preview.public_url} target="_blank" rel="noreferrer" className="text-xs px-3 py-1.5 rounded-btn font-semibold flex items-center gap-1" style={{ background: 'var(--bg)', color: 'var(--ink)', border: '1px solid var(--border)' }}>
                  <Download size={11} /> Download
                </a>
                <button onClick={() => del(preview.id)} className="text-xs p-1.5 rounded-btn" style={{ color: '#B34A3A' }} data-testid="doc-delete"><Trash2 size={14} /></button>
                <button onClick={() => setPreview(null)} style={{ color: 'var(--muted)' }}><X size={18} /></button>
              </div>
            </div>
            <div className="flex-1 overflow-auto flex items-center justify-center p-4" style={{ background: '#1A2428' }}>
              {kindOf(preview.content_type) === 'video' ? (
                <div className="w-full max-w-5xl">
                  <AnnotatedVideoPlayer upload={preview} onUpdated={reload} isOperator={true} />
                </div>
              ) : kindOf(preview.content_type) === 'image' ? (
                <img src={preview.public_url} alt={preview.original_name} className="max-w-full max-h-[70vh] object-contain" />
              ) : (
                <iframe src={preview.public_url} title={preview.original_name} className="w-full" style={{ height: '70vh', background: 'white', border: 0 }} />
              )}
            </div>
            <div className="p-3 text-xs font-mono truncate" style={{ color: 'var(--muted)', borderTop: '1px solid var(--border)' }}>
              {preview.public_url}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
